///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Sign-in surface for the Capacitor apps, rendered instead of AuthDialog
/// when isNativeApp() (documentation/mobile/NATIVE_AUTH_PLAN.md). A bottom
/// sheet with the native providers - the OS draws the actual credential UI
/// (Sign in with Apple sheet, Google account picker, passkey sheet) - plus
/// passkey account creation behind a collapsed row. No Turnstile: the
/// server rate-limits native clients instead (api-server utils/turnstile.ts).
///

import { Button } from "@/components/ui/button"
import { Icons } from "@/components/ui/icons"
import { Input } from "@/components/ui/input"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { toast } from "@/components/ui/use-toast"
import Link from "@/components/utils/Link"
import { applyNativeSignin, isGoogleSigninConfigured, signInWithApple, signInWithGoogle, type NativeSigninResult } from "@/native"
import { setAuthOpen, setDeviceLimitToken } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import {
	useAuthenticateWithPasskeyMutation,
	useGetPasskeyAuthenticationOptionsMutation,
	useRegisterWithPasskeyCompleteMutation,
	useRegisterWithPasskeyInitMutation,
} from "@/redux/userApi"
import { track } from "@/utils/analytics"
import { getDeviceId } from "@/utils/deviceId"
import { extractErrorMessage } from "@/utils/error-handler"
import { authenticateWithPasskey, isWebAuthnSupported, registerPasskey } from "@/utils/passkey"
import { ChevronDownIcon, KeyRoundIcon, Loader2 } from "lucide-react"
import { useEffect, useState } from "react"

type Busy = "apple" | "google" | "passkey" | "register" | null

export function NativeAuthSheet() {
	const dispatch = useAppDispatch()
	const open = useAppSelector(state => state.dialog.authOpen)
	const [busy, setBusy] = useState<Busy>(null)
	const [showCreate, setShowCreate] = useState(false)
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [email, setEmail] = useState("")

	const [getPasskeyAuthOptions] = useGetPasskeyAuthenticationOptionsMutation()
	const [authenticatePasskey] = useAuthenticateWithPasskeyMutation()
	const [registerWithPasskeyInit] = useRegisterWithPasskeyInitMutation()
	const [registerWithPasskeyComplete] = useRegisterWithPasskeyCompleteMutation()

	const passkeysAvailable = isWebAuthnSupported()
	const googleAvailable = isGoogleSigninConfigured()

	useEffect(() => {
		if (open) track("auth_dialog_opened", { rung: 0 })
		else setShowCreate(false)
	}, [open])

	const finish = (result: NativeSigninResult, provider: string) => {
		applyNativeSignin(result)
		if (result.status === "error") {
			toast({ title: `${provider} sign-in failed`, description: result.message, variant: "destructive" })
		}
	}

	const onApple = async () => {
		setBusy("apple")
		try {
			finish(await signInWithApple(), "Apple")
		} finally {
			setBusy(null)
		}
	}

	const onGoogle = async () => {
		setBusy("google")
		try {
			finish(await signInWithGoogle(), "Google")
		} finally {
			setBusy(null)
		}
	}

	const onPasskey = async () => {
		setBusy("passkey")
		try {
			const options = await getPasskeyAuthOptions()
			if (!options.data) {
				throw new Error(options.error ? extractErrorMessage(options.error, "Could not start passkey sign-in") : "No response from server")
			}
			const credential = await authenticateWithPasskey(options.data)
			const result = await authenticatePasskey({ credential, turnstileToken: undefined })
			if (result.data) {
				dispatch(setAuthOpen(false))
				return
			}
			const err = result.error as { status?: number; data?: { message?: string; evictionToken?: string } } | undefined
			if (err?.status === 409 && err.data?.evictionToken) {
				dispatch(setAuthOpen(false))
				dispatch(setDeviceLimitToken(err.data.evictionToken))
				return
			}
			throw new Error(err?.data?.message ?? "Passkey sign-in failed")
		} catch (error) {
			if (!isUserCancel(error)) {
				toast({
					title: "Passkey sign-in failed",
					description: error instanceof Error ? error.message : "Authentication failed",
					variant: "destructive",
				})
			}
		} finally {
			setBusy(null)
		}
	}

	const onCreateWithPasskey = async () => {
		if (!firstName.trim() || !lastName.trim() || !email.includes("@")) {
			toast({ title: "Missing information", description: "Please enter your name and email address.", variant: "destructive" })
			return
		}
		setBusy("register")
		try {
			const init = await registerWithPasskeyInit({
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				email: email.trim(),
				deviceFingerprint: getDeviceId(),
				turnstileToken: "",
			})
			if ("error" in init && init.error) {
				throw new Error(extractErrorMessage(init.error, "Could not start registration"))
			}
			if (!init.data) {
				throw new Error("No registration options received")
			}
			const credential = await registerPasskey(init.data)
			const complete = await registerWithPasskeyComplete({ credential })
			if ("error" in complete && complete.error) {
				throw new Error(extractErrorMessage(complete.error, "Could not complete registration"))
			}
			dispatch(setAuthOpen(false))
			toast({
				title: "Account created",
				description: `Check ${complete.data?.email ?? email} for a verification link to finish setting up.`,
				duration: 8000,
			})
		} catch (error) {
			if (!isUserCancel(error)) {
				toast({
					title: "Could not create account",
					description: error instanceof Error ? error.message : "Registration failed",
					variant: "destructive",
				})
			}
		} finally {
			setBusy(null)
		}
	}

	return (
		<Sheet open={open} onOpenChange={(next) => { if (busy === null) dispatch(setAuthOpen(next)) }}>
			<SheetContent
				side="bottom"
				className="rounded-t-3xl border-0 bg-background/95 backdrop-blur-xl px-5 pt-5 pb-[calc(1.25rem+var(--safe-area-inset-bottom))]"
				aria-describedby="native-auth-description"
			>
				<SheetHeader className="text-left space-y-1 mb-4">
					<SheetTitle className="text-lg font-bold">Sign in to EarthBrowser</SheetTitle>
					<SheetDescription id="native-auth-description" className="text-xs text-muted-foreground">
						Save views and places, set location alerts, and keep them on every device.
					</SheetDescription>
				</SheetHeader>

				<div className="flex flex-col gap-3">
					{/* Apple first: HIG wording and black/white treatment */}
					<Button
						size="lg"
						className="w-full h-12 rounded-xl bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90 text-sm font-semibold"
						disabled={busy !== null}
						onClick={() => void onApple()}
					>
						{busy === "apple" ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Icons.apple className="mr-2 h-5 w-5" />}
						Sign in with Apple
					</Button>

					{googleAvailable && (
						<Button
							size="lg"
							variant="outline"
							className="w-full h-12 rounded-xl text-sm font-semibold bg-background border-border/60"
							disabled={busy !== null}
							onClick={() => void onGoogle()}
						>
							{busy === "google" ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Icons.googleLogo className="mr-2 h-5 w-5" />}
							Continue with Google
						</Button>
					)}

					{passkeysAvailable && (
						<Button
							size="lg"
							variant="outline"
							className="w-full h-12 rounded-xl text-sm font-semibold bg-background border-border/60"
							disabled={busy !== null}
							onClick={() => void onPasskey()}
						>
							{busy === "passkey" ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <KeyRoundIcon className="mr-2 h-5 w-5" />}
							Sign in with a passkey
						</Button>
					)}
				</div>

				{passkeysAvailable && (
					<div className="mt-4">
						<button
							type="button"
							className="flex w-full items-center justify-between text-xs text-muted-foreground py-2"
							onClick={() => setShowCreate(v => !v)}
							aria-expanded={showCreate}
						>
							<span>New here? Create an account with a passkey</span>
							<ChevronDownIcon className={`h-4 w-4 transition-transform ${showCreate ? "rotate-180" : ""}`} />
						</button>
						{showCreate && (
							<div className="flex flex-col gap-2 mt-1">
								<div className="grid grid-cols-2 gap-2">
									<Input placeholder="First name" autoComplete="given-name" value={firstName} onChange={e => setFirstName(e.target.value)} disabled={busy !== null} />
									<Input placeholder="Last name" autoComplete="family-name" value={lastName} onChange={e => setLastName(e.target.value)} disabled={busy !== null} />
								</div>
								<Input type="email" placeholder="you@example.com" autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} disabled={busy !== null} />
								<Button
									size="lg"
									className="w-full h-11 rounded-xl text-sm font-semibold"
									disabled={busy !== null}
									onClick={() => void onCreateWithPasskey()}
								>
									{busy === "register" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRoundIcon className="mr-2 h-4 w-4" />}
									Create account
								</Button>
							</div>
						)}
					</div>
				)}

				<p className="mt-4 text-[11px] leading-relaxed text-muted-foreground text-center">
					By continuing you agree to the{" "}
					<Link href="/terms-and-conditions" className="underline">Terms</Link> and{" "}
					<Link href="/privacy" className="underline">Privacy Policy</Link>.
				</p>
			</SheetContent>
		</Sheet>
	)
}

/** WebAuthn surfaces a NotAllowedError / AbortError when the user dismisses the OS sheet */
function isUserCancel(error: unknown): boolean {
	if (error instanceof DOMException) {
		return error.name === "NotAllowedError" || error.name === "AbortError"
	}
	const message = error instanceof Error ? error.message.toLowerCase() : ""
	return message.includes("cancel") || message.includes("not allowed")
}
